/**
 * Weekly Planner — Phase 1.5
 * Drag unscheduled tasks onto Activity Blocks alongside read-only Google Calendar events.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Plus, RefreshCw, CalendarRange, Zap, Sparkles } from 'lucide-react';
import { DayColumn } from '../components/planner/DayColumn';
import { TaskTray } from '../components/planner/TaskTray';
import { AddBlockModal } from '../components/planner/AddBlockModal';
import { BlockDetailModal } from '../components/planner/BlockDetailModal';
import { CATEGORY_META } from '../components/planner/plannerConstants';

function mondayOf(date) {
  const d = new Date(date);
  const diff = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export default function WeeklyPlannerPage() {
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [openBlock, setOpenBlock] = useState(null);
  const [building, setBuilding] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      const iso = weekStart.toISOString().slice(0, 10);
      const d = await api.getPlannerWeek(iso);
      setData(d);
    } catch (e) {
      toast.error('Failed to load planner');
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  const days = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const today = new Date();
  const isSameDay = (a, b) => a.toDateString() === b.toDateString();

  const eventsForDay = (day) => (data?.calendar_events || []).filter((ev) => {
    const start = new Date(ev.start?.dateTime || ev.start?.date);
    return isSameDay(start, day);
  });

  const blocksForDay = (day) => (data?.blocks || []).filter((b) => isSameDay(new Date(b.start), day));

  const handleDropTask = async (taskId, start, targetBlock) => {
    try {
      if (targetBlock) {
        await api.assignTaskToBlock(targetBlock.id, taskId);
      } else {
        await api.quickPlaceTask(taskId, { start: start.toISOString() });
      }
      toast.success('Task scheduled');
      setSelectedTaskId(null);
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to schedule task');
    }
  };

  const handleMoveBlock = async (blockId, newStart) => {
    const block = (data?.blocks || []).find((b) => b.id === blockId);
    if (!block) return;
    const duration = new Date(block.end) - new Date(block.start);
    const newEnd = new Date(newStart.getTime() + duration);
    try {
      await api.updatePlannerBlock(blockId, { start: newStart.toISOString(), end: newEnd.toISOString() });
      load();
    } catch (e) {
      toast.error('Failed to move block');
    }
  };

  const handleSlotClick = (day, hour) => {
    if (!selectedTaskId) return;
    const start = new Date(day);
    start.setHours(hour, 0, 0, 0);
    handleDropTask(selectedTaskId, start, null);
  };

  const handleBuildWeek = async () => {
    setBuilding(true);
    try {
      const iso = weekStart.toISOString().slice(0, 10);
      const res = await api.buildMyWeek(iso);
      if (res.blocks_created > 0) {
        const breakdown = Object.entries(res.summary || {})
          .map(([cat, hrs]) => `${CATEGORY_META[cat]?.label || cat}: ${hrs}h`)
          .join(' · ');
        toast.success(res.message, { description: breakdown || undefined, duration: 6000 });
      } else {
        toast.info(res.message);
      }
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to build your week');
    } finally {
      setBuilding(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-muted-foreground" data-testid="planner-loading">Loading your week…</div>;
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3" data-testid="planner-error-state">
        <p className="text-muted-foreground">Couldn't load your Weekly Planner.</p>
        <Button onClick={load} data-testid="planner-retry-btn"><RefreshCw className="h-4 w-4 mr-2" /> Retry</Button>
      </div>
    );
  }

  const money = data?.money_action_summary || { scheduled_money_hours: 0, scheduled_total_hours: 0 };
  const moneyPct = money.scheduled_total_hours > 0 ? Math.round((money.scheduled_money_hours / money.scheduled_total_hours) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="planner-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2"><CalendarRange className="h-6 w-6" /> Weekly Planner</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Money-Action Hours: <span className="font-semibold text-foreground">{money.scheduled_money_hours}h</span> of {money.scheduled_total_hours}h scheduled ({moneyPct}%)
            {!data.calendar_connected && <span className="ml-2 text-amber-600">· Connect Google Calendar in Settings to see conflicts</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={() => setWeekStart((w) => { const d = new Date(w); d.setDate(d.getDate() - 7); return d; })} data-testid="planner-prev-week-btn">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={() => setWeekStart(mondayOf(new Date()))} data-testid="planner-today-btn">This Week</Button>
          <Button variant="outline" size="icon" onClick={() => setWeekStart((w) => { const d = new Date(w); d.setDate(d.getDate() + 7); return d; })} data-testid="planner-next-week-btn">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={load} data-testid="planner-refresh-btn"><RefreshCw className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => setShowAddBlock(true)} data-testid="add-block-btn"><Plus className="h-4 w-4 mr-2" /> Add Block</Button>
          <Button onClick={handleBuildWeek} disabled={building} data-testid="build-my-week-btn">
            <Sparkles className="h-4 w-4 mr-2" /> {building ? 'Building…' : 'Build My Week'}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-[11px]">
        {Object.entries(CATEGORY_META).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1 px-2 py-0.5 rounded-full border border-border">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: v.color }} />
            {v.label}{v.money_action && <Zap className="h-2.5 w-2.5 text-amber-500" />}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4">
        <TaskTray tasks={data?.unscheduled_tasks || []} selectedTaskId={selectedTaskId} onSelect={setSelectedTaskId} />

        {/* Mobile: single day with tabs */}
        <div className="md:hidden">
          <MobileDayPicker days={days} today={today} isSameDay={isSameDay}>
            {(activeDay) => (
              <DayColumn
                dayDate={activeDay} blocks={blocksForDay(activeDay)} events={eventsForDay(activeDay)}
                workStartHour={8} workEndHour={18} selectedTaskId={selectedTaskId}
                onDropTask={handleDropTask} onMoveBlock={handleMoveBlock}
                onSlotClick={(hour) => handleSlotClick(activeDay, hour)} onOpenBlock={setOpenBlock}
                isToday={isSameDay(activeDay, today)}
              />
            )}
          </MobileDayPicker>
        </div>

        {/* Desktop: 6-day grid */}
        <div className="hidden md:flex gap-2" data-testid="planner-week-grid">
          {days.map((d) => (
            <DayColumn
              key={d.toISOString()} dayDate={d} blocks={blocksForDay(d)} events={eventsForDay(d)}
              workStartHour={8} workEndHour={18} selectedTaskId={selectedTaskId}
              onDropTask={handleDropTask} onMoveBlock={handleMoveBlock}
              onSlotClick={(hour) => handleSlotClick(d, hour)} onOpenBlock={setOpenBlock}
              isToday={isSameDay(d, today)}
            />
          ))}
        </div>
      </div>

      <AddBlockModal open={showAddBlock} onClose={() => setShowAddBlock(false)} weekStart={weekStart} onCreated={load} />
      <BlockDetailModal block={openBlock} onClose={() => setOpenBlock(null)} onChanged={load} />
    </div>
  );
}

function MobileDayPicker({ days, today, isSameDay, children }) {
  const [idx, setIdx] = useState(days.findIndex((d) => isSameDay(d, today)) >= 0 ? days.findIndex((d) => isSameDay(d, today)) : 0);
  return (
    <div>
      <div className="flex gap-1.5 overflow-x-auto pb-2" data-testid="mobile-day-tabs">
        {days.map((d, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            data-testid={`mobile-day-tab-${i}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border ${
              idx === i ? 'bg-primary text-primary-foreground border-primary' : 'border-border'
            }`}
          >
            {d.toLocaleDateString([], { weekday: 'short' })} {d.getDate()}
          </button>
        ))}
      </div>
      {children(days[idx])}
    </div>
  );
}
